import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, getJsonAuthHeaders } from "../../utils/api";
import TeacherAcademicHeader from "./TeacherAcademicHeader";

function TeacherRemarks({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const location = useLocation();

  const lockedCourse =
    location.state?.course ||
    courses.find((c) => String(c.id) === String(assignmentId)) ||
    null;

  const lockedCourseId = lockedCourse ? String(lockedCourse.course_id || lockedCourse.id || "") : "";
  const lockedClassId = lockedCourse ? String(lockedCourse.class_id || "") : "";

  const [students, setStudents] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("students");
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [assignMode, setAssignMode] = useState("single");
  const [studentId, setStudentId] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkDate, setRemarkDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editDate, setEditDate] = useState("");

  const loadStudents = async () => {
    const params = new URLSearchParams();
    if (lockedClassId) params.set("class_id", lockedClassId);
    if (lockedCourseId) params.set("course_id", lockedCourseId);
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/students?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = res.ok ? await res.json() : [];
    setStudents(Array.isArray(data) ? data : []);
  };

  const loadRemarks = async () => {
    const params = new URLSearchParams();
    if (lockedCourseId) params.set("course_id", lockedCourseId);
    if (lockedClassId) params.set("class_id", lockedClassId);
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/remarks?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = res.ok ? await res.json() : [];
    setRemarks(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadStudents(), loadRemarks()]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCourseId, lockedClassId]);

  const remarkCountByStudent = useMemo(() => {
    const counts = {};
    remarks.forEach((r) => {
      counts[r.student_id] = (counts[r.student_id] || 0) + 1;
    });
    return counts;
  }, [remarks]);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId) || null,
    [students, selectedStudentId],
  );

  const studentRemarks = useMemo(() => {
    if (!selectedStudentId) return [];
    return remarks.filter((r) => r.student_id === selectedStudentId);
  }, [remarks, selectedStudentId]);

  const filteredStudents = useMemo(() => {
    if (!lockedCourseId) return students;
    return students.filter(
      (s) => !s.course_id || String(s.course_id) === String(lockedCourseId),
    );
  }, [students, lockedCourseId]);

  const toggleStudent = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const openAddRemark = (preselectId = null) => {
    setView("add");
    setAssignMode(preselectId ? "single" : "single");
    setStudentId(preselectId ? String(preselectId) : "");
    setSelectedStudents([]);
    setRemark("");
    setRemarkDate(new Date().toISOString().slice(0, 10));
  };

  const openStudentRemarks = (id) => {
    setSelectedStudentId(id);
    setView("student");
    setEditingId(null);
  };

  const backToList = () => {
    setView("students");
    setSelectedStudentId(null);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!remark.trim()) return;
    setSaving(true);
    try {
      if (assignMode === "single") {
        if (!studentId) {
          alert("Select a student");
          setSaving(false);
          return;
        }
        const res = await fetch(`${API_BASE_URL}/api/teachers/me/remarks`, {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({
            student_id: Number(studentId),
            remark: remark.trim(),
            course_id: lockedCourseId ? Number(lockedCourseId) : null,
            class_id: lockedClassId ? Number(lockedClassId) : null,
            remark_date: remarkDate || null,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to save remark");
        }
      } else {
        const ids =
          assignMode === "bulk"
            ? filteredStudents.map((s) => s.id)
            : selectedStudents;
        if (!ids.length) {
          alert("Select at least one student");
          setSaving(false);
          return;
        }
        const res = await fetch(`${API_BASE_URL}/api/teachers/me/remarks/bulk`, {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({
            student_ids: ids,
            remark: remark.trim(),
            course_id: lockedCourseId ? Number(lockedCourseId) : null,
            class_id: lockedClassId ? Number(lockedClassId) : null,
            remark_date: remarkDate || null,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to save remarks");
        }
      }
      setRemark("");
      setSelectedStudents([]);
      setStudentId("");
      await loadRemarks();
      setView("students");
    } catch (err) {
      alert(err.message || "Failed to save remark");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditText(r.remark);
    setEditDate(r.remark_date || "");
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/teachers/me/remarks/${editingId}`, {
        method: "PUT",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          remark: editText.trim(),
          remark_date: editDate || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to update remark");
      }
      setEditingId(null);
      setEditText("");
      await loadRemarks();
    } catch (err) {
      alert(err.message || "Failed to update remark");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this remark?")) return;
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/remarks/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (res.ok) loadRemarks();
  };

  const subtitle =
    lockedCourse
      ? `${lockedCourse.course_name || lockedCourse.name} · ${lockedCourse.class_name || "Class"}`
      : "Academic and behavioral remarks for your students.";

  const backToCourse = () =>
    navigate(`/teacher/courses/${assignmentId}`, { state: { course: lockedCourse } });

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading remarks…</p>
      </div>
    );
  }

  return (
    <div className="tp-academic-shell">
      <TeacherAcademicHeader
        title="Student Remarks"
        subtitle={subtitle}
        backLabel="Back to course"
        onBack={assignmentId ? backToCourse : null}
        actions={
          view === "students" ? (
            <button type="button" className="tp-btn tp-btn-primary" onClick={() => openAddRemark()}>
              + Add Remark
            </button>
          ) : (
            <button type="button" className="tp-btn tp-btn--ghost" onClick={backToList}>
              ← Student list
            </button>
          )
        }
      />

      <div className="tp-academic-panel">
        {view === "students" && (
          <>
            <div className="tp-academic-panel__head">
              <h2>Students</h2>
              <span className="tp-badge">{filteredStudents.length} enrolled</span>
            </div>
            {filteredStudents.length === 0 ? (
              <p className="tp-muted">No students in this class.</p>
            ) : (
              <div className="tp-student-pick-list">
                {filteredStudents.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="tp-student-pick-card"
                    onClick={() => openStudentRemarks(s.id)}
                  >
                    <div className="tp-student-pick-card__main">
                      <strong>{s.name}</strong>
                      <span className="tp-mono">{s.student_id}</span>
                    </div>
                    <div className="tp-student-pick-card__meta">
                      {s.class_section ? (
                        <span className="tp-chip tp-chip--section">{s.class_section}</span>
                      ) : null}
                      <span className="tp-chip tp-chip--class">
                        {remarkCountByStudent[s.id] || 0} remark{(remarkCountByStudent[s.id] || 0) === 1 ? "" : "s"}
                      </span>
                    </div>
                    <span className="tp-student-pick-card__cta">View remarks →</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {view === "student" && selectedStudent && (
          <>
            <div className="tp-academic-panel__head">
              <div>
                <h2>{selectedStudent.name}</h2>
                <p className="tp-muted">{selectedStudent.student_id}</p>
              </div>
              <button
                type="button"
                className="tp-btn tp-btn-primary tp-btn--sm"
                onClick={() => openAddRemark(selectedStudent.id)}
              >
                + Add remark
              </button>
            </div>
            {studentRemarks.length === 0 ? (
              <p className="tp-muted">No remarks for this student yet.</p>
            ) : (
              <div className="tp-remark-feed">
                {studentRemarks.map((r) => (
                  <article key={r.id} className="tp-remark-card">
                    <div className="tp-remark-card__head">
                      <time>{r.remark_date}</time>
                      <span className="tp-muted">
                        {[r.course_name, r.class_name].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    {editingId === r.id ? (
                      <div className="tp-remark-card__edit">
                        <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                        <textarea
                          className="tp-textarea"
                          rows={3}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="tp-table-actions">
                          <button type="button" className="tp-btn tp-btn-primary tp-btn--sm" onClick={saveEdit} disabled={saving}>
                            Save
                          </button>
                          <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => setEditingId(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="tp-remark-card__body">{r.remark}</p>
                        <div className="tp-table-actions">
                          <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => startEdit(r)}>
                            Edit
                          </button>
                          <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => remove(r.id)}>
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {view === "add" && (
          <>
            <div className="tp-academic-panel__head">
              <h2>Add Remark</h2>
              <p className="tp-muted">Single student, selected students, or whole class.</p>
            </div>
            <form onSubmit={submit} className="tp-form-grid tp-form-grid--academic">
              <label>
                Assign to
                <select value={assignMode} onChange={(e) => setAssignMode(e.target.value)}>
                  <option value="single">Single student</option>
                  <option value="selected">Multiple selected students</option>
                  <option value="bulk">Whole class (bulk)</option>
                </select>
              </label>
              <label>
                Date
                <input type="date" value={remarkDate} onChange={(e) => setRemarkDate(e.target.value)} />
              </label>
              {assignMode === "single" && (
                <label className="tp-form-full">
                  Student
                  <select required value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                    <option value="">Select student</option>
                    {filteredStudents.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.student_id})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {assignMode === "selected" && (
                <div className="tp-form-full">
                  <span className="tp-field-label">Select students</span>
                  <div className="tp-student-checks tp-student-checks--boxed">
                    {filteredStudents.map((s) => (
                      <label key={s.id}>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(s.id)}
                          onChange={() => toggleStudent(s.id)}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="tp-form-full">
                Remark
                <textarea
                  className="tp-textarea tp-textarea--lg"
                  required
                  rows={5}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Write a remark for the student(s)…"
                />
              </label>
              <div className="tp-form-full tp-form-actions">
                <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Save Remark"}
                </button>
                <button type="button" className="tp-btn tp-btn--ghost" onClick={backToList}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default TeacherRemarks;
